<template>
    <teleport to="body">
        <Modal
            :is-open="showLoungeFilterModal"
            title="All filters"
            @close="() => setShowLoungeFilterModal(false)"
        >
            <template #content>
                <!-- TERMINALS FILTER -->
                <FilterModalSection
                    title="Terminals"
                    v-if="availableTerminals?.length"
                >
                    <template #options>
                        <FilterListItem
                            v-for="terminal in availableTerminals"
                            :key="terminal.value"
                            labelKey="value"
                            valueKey="value"
                            :checked="isChecked(terminal, 'terminals')"
                            :option="terminal"
                            @toggleSelect="
                                () =>
                                    toggleSelectFilter(
                                        terminal,
                                        'terminals',
                                        'value'
                                    )
                            "
                        />
                    </template>
                </FilterModalSection>
                <!-- AMENITIES FILTER -->
                <FilterModalSection
                    title="Amenities"
                    v-if="availableAmenities?.length"
                >
                    <template #options>
                        <FilterListItem
                            v-for="amenity in availableAmenities"
                            :key="amenity.value"
                            labelKey="label"
                            valueKey="value"
                            :icon="amenity.icon"
                            :option="amenity"
                            :checked="isChecked(amenity, 'amenities')"
                            @toggleSelect="
                                () =>
                                    toggleSelectFilter(
                                        amenity,
                                        'amenities',
                                        'value'
                                    )
                            "
                        />
                    </template>
                </FilterModalSection>
                <!-- OPEN STATUS FILTER -->
                <FilterModalSection title="Open">
                    <template #options>
                        <FilterListItem
                            v-for="option in openOptions"
                            :key="option.value"
                            :option="option"
                            labelKey="label"
                            valueKey="value"
                            :checked="isChecked(option, 'openStatus')"
                            @toggleSelect="
                                () =>
                                    toggleSelectFilter(
                                        option,
                                        'openStatus',
                                        'value'
                                    )
                            "
                        />
                    </template>
                </FilterModalSection>
                <!-- CREDIT CARD FILTER -->
                <FilterModalSection
                    title="Credit card access"
                    v-if="searchType === 'Trip wizard' && creditCards?.length"
                >
                    <template #options>
                        <FilterListItem
                            v-for="card in creditCards"
                            :key="card.code"
                            :option="card"
                            labelKey="title"
                            valueKey="code"
                            :checked="isChecked(card, 'cards', 'code')"
                            @toggleSelect="
                                () => toggleSelectFilter(card, 'cards', 'code')
                            "
                        />
                    </template>
                </FilterModalSection>
                <!-- AIRLINE FILTER -->
                <FilterModalSection
                    title="Airline access"
                    v-if="availableAirlines?.length"
                >
                    <template #options>
                        <FilterListItem
                            v-for="a in availableAirlines"
                            :key="a.code"
                            :option="a"
                            labelKey="name"
                            valueKey="value"
                            :checked="isChecked(a, 'airlines', 'value')"
                            @toggleSelect="
                                () => toggleSelectFilter(a, 'airlines', 'value')
                            "
                        />
                    </template>
                </FilterModalSection>
                <!-- CABIN CLASS FILTER -->
                <FilterModalSection title="Cabin class access">
                    <template #options>
                        <FilterListItem
                            v-for="classItem in classes"
                            :key="classItem.value"
                            :option="classItem"
                            labelKey="name"
                            valueKey="value"
                            :checked="isChecked(classItem, 'classes')"
                            @toggleSelect="
                                () =>
                                    toggleSelectFilter(
                                        classItem,
                                        'classes',
                                        'value'
                                    )
                            "
                        />
                    </template>
                </FilterModalSection>
                <!-- FLYER STATUS FILTER -->
                <FilterModalSection
                    title="Flyer status access"
                    v-if="
                        searchType === 'Trip wizard' &&
                        statusesByAirline?.length
                    "
                >
                    <template #options>
                        <div
                            v-for="(airline, index) in statusesByAirline"
                            :key="`header-${index}`"
                            class="flex gap-4 flex-col"
                        >
                            <div
                                class="text-slate-800 text-sm font-medium font-['Inter'] leading-tight"
                            >
                                {{ airline.full_name }}
                            </div>
                            <FilterListItem
                                v-for="(
                                    ffp, i
                                ) in airline.frequent_flyer_programs"
                                :key="`ffp-${i}`"
                                :option="ffp"
                                labelKey="status_extended"
                                valueKey="status_code"
                                :checked="
                                    isChecked(
                                        ffp,
                                        'flyerStatuses',
                                        'status_code'
                                    )
                                "
                                @toggleSelect="
                                    () =>
                                        toggleSelectFilter(
                                            ffp,
                                            'flyerStatuses',
                                            'status_code'
                                        )
                                "
                            />
                        </div>
                    </template>
                </FilterModalSection>
                <!-- MEMBERSHIP FILTER -->
                <FilterModalSection
                    title="Membership access"
                    v-if="
                        searchType === 'Trip wizard' &&
                        loungeMemberships?.length
                    "
                >
                    <template #options>
                        <FilterListItem
                            v-for="membershipItem in loungeMemberships"
                            :key="membershipItem.value"
                            :option="membershipItem"
                            labelKey="name"
                            valueKey="value"
                            :checked="isChecked(membershipItem, 'memberships')"
                            @toggleSelect="
                                () =>
                                    toggleSelectFilter(
                                        membershipItem,
                                        'memberships',
                                        'value'
                                    )
                            "
                        />
                    </template>
                </FilterModalSection>
                <!-- SPECIAL STATUS FILTER -->
                <FilterModalSection
                    title="Special status access"
                    v-if="searchType === 'Trip wizard'"
                >
                    <template #options>
                        <FilterListItem
                            v-for="specialStatusItem in specialCircumstances"
                            :key="specialStatusItem.value"
                            :option="specialStatusItem"
                            labelKey="name"
                            valueKey="code"
                            :checked="
                                isChecked(
                                    specialStatusItem,
                                    'specialStatuses',
                                    'code'
                                )
                            "
                            @toggleSelect="
                                () =>
                                    toggleSelectFilter(
                                        specialStatusItem,
                                        'specialStatuses',
                                        'code'
                                    )
                            "
                        />
                    </template>
                </FilterModalSection>
            </template>
            <template #footer>
                <div class="justify-between flex w-full">
                    <button
                        type="button"
                        class="px-4 py-2 text-slate-800 text-sm font-medium bg-white rounded shadow border border-slate-200 justify-center items-center hover:border-slate-300 hover:bg-slate-200 active:border-slate-300 active:bg-slate-200 focus:outline-none focus:border-2"
                        @click="resetFilters"
                    >
                        Clear all
                    </button>
                    <button
                        class="px-4 py-2.5 bg-orange-600 rounded justify-start items-start gap-2 flex text-white text-sm font-medium font-['Inter'] leading-tight hover:bg-red-400 active:bg-red-400 focus:outline-slate-200 focus:shadow disabled:opacity-50 disabled:bg-orange-600 cursor-pointer"
                        @click="applyFilters"
                        :disabled="false"
                    >
                        Show lounges
                    </button>
                </div>
            </template>
        </Modal>
    </teleport>
</template>

<script>
import { ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useLoungesStore } from '@/store/lounges';
import Modal from '@/components/ui/modal/ui-modal.vue';
import FilterModalSection from '@/components/tools/lounges/partials/filter-modal-section/filter-modal-section.vue';
import FilterListItem from '@/components/tools/lounges/partials/filter-list-item/filter-list-item.vue';
export default {
    name: 'LoungeFilterModal',
    components: {
        Modal,
        FilterModalSection,
        FilterListItem,
    },
    setup() {
        const loungesStore = useLoungesStore();
        const {
            showLoungeFilterModal,
            availableAirlines,
            availableAmenities,
            availableTerminals,
            classes,
            openOptions,
            creditCards,
            statusesByAirline,
            loungeMemberships,
            searchType,
            specialCircumstances,
        } = storeToRefs(loungesStore);
        const { setFilters, setShowLoungeFilterModal } = loungesStore;

        const localFilters = ref({
            terminals: [],
            airlines: [],
            amenities: [],
            openStatus: [],
            cards: [],
            classes: [],
            flyerStatuses: [],
            memberships: [],
            specialStatuses: [],
        });

        const isChecked = (item, type, key = 'value') => {
            return localFilters.value[type].some((el) => el[key] === item[key]);
        };

        const toggleSelectFilter = (item, type, key = 'value') => {
            if (isChecked(item, type)) {
                localFilters.value[type] = localFilters.value[type].filter(
                    (el) => el[key] !== item[key]
                );
            } else {
                localFilters.value[type].push(item);
            }
        };

        const applyFilters = () => {
            setFilters(localFilters.value);
            setShowLoungeFilterModal(false);
        };

        const resetFilters = () => {
            Object.keys(localFilters.value).forEach((key) => {
                localFilters.value[key] = [];
            });
        };

        return {
            applyFilters,
            availableAirlines,
            availableAmenities,
            availableTerminals,
            classes,
            isChecked,
            localFilters,
            openOptions,
            resetFilters,
            searchType,
            setShowLoungeFilterModal,
            showLoungeFilterModal,
            creditCards,
            statusesByAirline,
            loungeMemberships,
            specialCircumstances,
            toggleSelectFilter,
        };
    },
};
</script>
